import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Loader2, Wrench } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

function BrandMark({ size = "h-9 w-9", textClass = "text-xl" }) {
    return (
        <div className="flex items-center gap-2.5">
            <span className={`grid ${size} place-items-center rounded-md bg-gradient-to-br from-emerald-500 to-blue-600 text-white`}>
                <Wrench className="h-[55%] w-[55%]" strokeWidth={2.5} />
            </span>
            <span className={`font-display ${textClass} font-semibold tracking-tight`}>AutoBrix</span>
        </div>
    );
}

export default function LoginPage() {
    const { user, login } = useAuth();
    const { t } = useT();
    const nav = useNavigate();
    const loc = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        document.title = "AutoBrix · Sign in";
        if (user) {
            let to = loc.state?.from?.pathname;
            if (!to) {
                to = user.is_platform_admin && !user.impersonated_by ? "/admin" : "/";
            }
            nav(to, { replace: true });
        }
    }, [user, nav, loc]);

    async function onSubmit(e) {
        e.preventDefault();
        setSubmitting(true);
        const r = await login(email, password);
        setSubmitting(false);
        if (!r.ok) {
            toast.error(r.message);
        }
    }

    return (
        <div className="grid min-h-screen grid-cols-1 lg:grid-cols-5" data-testid="login-page">
            {/* Left: dark editorial hero */}
            <div className="relative order-1 hidden overflow-hidden bg-brand-black text-white lg:order-none lg:col-span-3 lg:flex lg:flex-col">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: "url('/brand/login-hero-india.jpg')" }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-[#0A0A0B]/75 to-[#0A0A0B]/20" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/90 via-transparent to-[#0A0A0B]/30" />
                {/* grain / vignette accent line */}
                <div className="absolute left-0 top-0 h-full w-1.5 bg-primary" />

                <div className="relative flex h-full flex-col justify-between p-12">
                    <BrandMark textClass="text-xl" />

                    <div className="max-w-xl">
                        <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary">
                            Workshop OS · multi-tenant · GST-ready
                        </div>
                        <h2 className="mt-5 font-display text-5xl font-semibold leading-[1.03] tracking-tight text-white sm:text-6xl">
                            Job cards on tablets.<br />
                            <span className="text-emerald-400">Invoices</span> that pass GST audits.
                        </h2>
                        <p className="mt-6 max-w-md text-base leading-relaxed text-white/70">
                            Designed for the shop floor. Built for the back office. One workspace for advisors,
                            technicians, parts and accounts.
                        </p>
                    </div>

                    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-white/60">
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
                        Service · Inspections · Insurance Claims
                    </div>
                </div>
            </div>

            {/* Right: form */}
            <div className="col-span-1 flex flex-col bg-background px-6 py-10 lg:col-span-2 lg:px-12">
                <div className="lg:hidden">
                    <BrandMark size="h-8 w-8" textClass="text-lg" />
                </div>
                <div className="hidden lg:block">
                    <BrandMark size="h-8 w-8" textClass="text-base" />
                </div>

                <div className="mx-auto my-auto w-full max-w-md py-12">
                    <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                        {t("login.title")}
                    </h1>
                    <p className="mt-2 text-sm text-muted-foreground">{t("login.subtitle")}</p>

                    <Card className="mt-8 p-6">
                        <form className="space-y-4" onSubmit={onSubmit}>
                            <div className="space-y-1.5">
                                <Label
                                    htmlFor="email"
                                    className="font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
                                >
                                    {t("login.email")}
                                </Label>
                                <Input
                                    id="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="login-email"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label
                                    htmlFor="password"
                                    className="font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
                                >
                                    {t("login.password")}
                                </Label>
                                <Input
                                    id="password"
                                    type="password"
                                    autoComplete="current-password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="login-password"
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={submitting}
                                data-testid="login-submit"
                                className="w-full"
                            >
                                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {t("login.submit")}
                            </Button>
                        </form>
                    </Card>

                    <div className="mt-6 text-center text-sm text-muted-foreground">
                        <Link
                            to="/register"
                            className="font-medium text-primary hover:underline"
                            data-testid="link-register"
                        >
                            {t("login.create")} →
                        </Link>
                    </div>
                </div>

                <div className="mt-auto pt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                    AutoBrix · Workshop OS · v0.2.5
                </div>
            </div>
        </div>
    );
}
